/**
 * Safe paths after sign-in / sign-up (blocks open redirects).
 */

import {
  PUBLIC_SIGN_IN_PATH,
  PUBLIC_AUTH_SIGNUP_QUERY_KEY,
  PUBLIC_AUTH_SIGNUP_QUERY_VALUE,
} from '../constants/publicAuthRoutes.constants'

export const POST_AUTH_DEFAULT_PATH = '/app/' as const

/** In-app squad join (must match Expo route + `buildWorldCupGroupJoinAppUrl`). */
export const WC_GROUP_JOIN_APP_PATH = '/app/worldcup/groups/join' as const

export interface BuildPublicSignInHrefOptions {
  signup?: boolean
  returnUrl?: unknown
  invite_token?: string
  reason?: string
}

/**
 * Single entry URL for auth: `/sign-in` with optional query flags (signup, returnUrl, invite_token, reason).
 */
export function buildPublicSignInHref(options?: BuildPublicSignInHrefOptions): string {
  const search = new URLSearchParams()
  if (options?.signup) {
    search.set(PUBLIC_AUTH_SIGNUP_QUERY_KEY, PUBLIC_AUTH_SIGNUP_QUERY_VALUE)
  }
  if (options?.invite_token) {
    search.set('invite_token', options.invite_token)
  }
  if (options?.reason) {
    search.set('reason', options.reason)
  }
  const rawReturn = options?.returnUrl
  const returnStr =
    typeof rawReturn === 'string' ? rawReturn : Array.isArray(rawReturn) ? rawReturn[0] : ''
  if (returnStr) {
    const safe = getSafePostAuthPath(returnStr)
    if (safe !== POST_AUTH_DEFAULT_PATH) {
      search.set('returnUrl', safe)
    }
  }
  const qs = search.toString()
  return qs ? `${PUBLIC_SIGN_IN_PATH}?${qs}` : PUBLIC_SIGN_IN_PATH
}

const MARKETING_RETURN_PATHS = new Set(['/', '/organizations', '/contact', '/packs', '/enterprise'])

const JOIN_CODE_PARAM_VALUE_RE = /^[A-Za-z0-9-]+$/

const MARKETING_HOME_SECTION_VALUES = new Set(['pricing', 'features'])

/** Stripe Checkout Session id (e.g. `cs_test_…`) or dashboard template `{CHECKOUT_SESSION_ID}`. */
const STRIPE_CONSUMER_RETURN_SESSION_ID_RE = /^[\w{}-]+$/

/**
 * `/?section=pricing` with optional Stripe return `billing` + `session_id` (allowlisted keys only).
 */
function getSafeMarketingHomePricingStripeReturnPath(trimmed: string): string | null {
  const qIndex = trimmed.indexOf('?')
  if (qIndex === -1) {
    return null
  }
  if (trimmed.slice(0, qIndex) !== '/') {
    return null
  }
  let params: URLSearchParams
  try {
    params = new URLSearchParams(trimmed.slice(qIndex + 1))
  } catch {
    return null
  }
  if (params.get('section') !== 'pricing') {
    return null
  }
  const billing = params.get('billing')
  if (billing !== 'success' && billing !== 'cancel') {
    return null
  }
  const allowedKeys = new Set(['section', 'billing', 'session_id'])
  for (const k of params.keys()) {
    if (!allowedKeys.has(k)) {
      return null
    }
  }
  const sessionId = params.get('session_id')
  if (sessionId != null) {
    const sid = sessionId.trim()
    if (sid.length < 2 || sid.length > 256 || !STRIPE_CONSUMER_RETURN_SESSION_ID_RE.test(sid)) {
      return null
    }
  }
  return trimmed
}

/**
 * Allows only `/?section=pricing` or `/?section=features` on `/` (single query key).
 */
function getSafeMarketingHomeSectionPath(trimmed: string): string | null {
  const qIndex = trimmed.indexOf('?')
  if (qIndex === -1) {
    return null
  }
  const pathname = trimmed.slice(0, qIndex)
  if (pathname !== '/') {
    return null
  }
  let params: URLSearchParams
  try {
    params = new URLSearchParams(trimmed.slice(qIndex + 1))
  } catch {
    return null
  }
  const keys = [...params.keys()]
  if (keys.length !== 1 || keys[0] !== 'section') {
    return null
  }
  const section = params.get('section')
  if (section == null || !MARKETING_HOME_SECTION_VALUES.has(section)) {
    return null
  }
  return trimmed
}

/**
 * Player invite return path: `/join?join=<sanitized code>` only (single query key, sanitized code).
 * Used when an unauthenticated user opens an invite link — sign-in returns them here and the
 * player-only join screen auto-joins without ever showing admin chrome.
 */
function getSafePublicJoinReturnPath(trimmed: string): string | null {
  if (!trimmed.startsWith('/join')) {
    return null
  }
  const q = trimmed.indexOf('?')
  if (q === -1) {
    return trimmed === '/join' ? '/join' : null
  }
  if (trimmed.slice(0, q) !== '/join') {
    return null
  }
  let params: URLSearchParams
  try {
    params = new URLSearchParams(trimmed.slice(q + 1))
  } catch {
    return null
  }
  const keys = [...params.keys()]
  if (keys.length === 0) {
    return '/join'
  }
  if (keys.length !== 1 || keys[0] !== 'join') {
    return null
  }
  const rawJoin = params.get('join')
  if (!rawJoin || !JOIN_CODE_PARAM_VALUE_RE.test(rawJoin.trim())) {
    return null
  }
  const normalized = rawJoin.replace(/-/g, '').trim().toUpperCase()
  if (normalized.length < 4 || normalized.length > 32 || !/^[A-Z0-9]+$/.test(normalized)) {
    return null
  }
  return `/join?join=${encodeURIComponent(normalized)}`
}

/**
 * Allows `/account-admin` and `/account-admin?join=<sanitized code>` only (no other query keys).
 */
function getSafeAccountAdminReturnPath(trimmed: string): string | null {
  if (!trimmed.startsWith('/account-admin')) {
    return null
  }
  const q = trimmed.indexOf('?')
  if (q === -1) {
    return trimmed === '/account-admin' ? '/account-admin' : null
  }
  if (trimmed.slice(0, q) !== '/account-admin') {
    return null
  }
  let params: URLSearchParams
  try {
    params = new URLSearchParams(trimmed.slice(q + 1))
  } catch {
    return null
  }
  const keys = [...params.keys()]
  if (keys.length === 0) {
    return '/account-admin'
  }
  if (keys.length !== 1 || keys[0] !== 'join') {
    return null
  }
  const rawJoin = params.get('join')
  if (!rawJoin || !JOIN_CODE_PARAM_VALUE_RE.test(rawJoin.trim())) {
    return null
  }
  const normalized = rawJoin.replace(/-/g, '').trim().toUpperCase()
  if (normalized.length < 4 || normalized.length > 32 || !/^[A-Z0-9]+$/.test(normalized)) {
    return null
  }
  return `/account-admin?join=${encodeURIComponent(normalized)}`
}

/**
 * World Cup join deep link: `?code=<JOIN_CODE>` is required; `&autojoin=1` is
 * optional and instructs the join screen to fire the join request immediately
 * on mount (used by the /wc-invite/<code> waiting-room flow). Any other query
 * key → bare join path (manual paste still works).
 */
function getSafeWorldCupGroupJoinReturnPath(trimmed: string): string | null {
  if (!trimmed.startsWith(WC_GROUP_JOIN_APP_PATH)) {
    return null
  }
  const q = trimmed.indexOf('?')
  if (q === -1) {
    return WC_GROUP_JOIN_APP_PATH
  }
  if (trimmed.slice(0, q) !== WC_GROUP_JOIN_APP_PATH) {
    return null
  }
  let params: URLSearchParams
  try {
    params = new URLSearchParams(trimmed.slice(q + 1))
  } catch {
    return WC_GROUP_JOIN_APP_PATH
  }
  const keys = [...params.keys()]
  if (keys.length === 0) {
    return WC_GROUP_JOIN_APP_PATH
  }
  const allowedKeys = new Set(['code', 'autojoin'])
  for (const k of keys) {
    if (!allowedKeys.has(k)) {
      return WC_GROUP_JOIN_APP_PATH
    }
  }
  const raw = params.get('code')
  if (!raw || !JOIN_CODE_PARAM_VALUE_RE.test(raw.trim())) {
    return WC_GROUP_JOIN_APP_PATH
  }
  const code = encodeURIComponent(raw.trim().toUpperCase())
  const autojoin = params.get('autojoin')
  // Only the literal "1" is preserved — any other value drops the flag silently
  // so callers can't smuggle arbitrary payloads through this safe path.
  const autojoinSuffix = autojoin === '1' ? '&autojoin=1' : ''
  return `${WC_GROUP_JOIN_APP_PATH}?code=${code}${autojoinSuffix}`
}

/**
 * `usePathname()` omits the query string — merge `code` for WC squad join so sign-in returnUrl stays deep-linked.
 * Also normalizes `/worldcup/groups/join` → `/app/worldcup/groups/join` when the router omits the `app` segment.
 */
export function buildAppReturnUrlForAuthGuard(
  pathname: string,
  searchParams: Record<string, unknown>,
): string {
  const path = pathname.trim() || POST_AUTH_DEFAULT_PATH
  const normalized = path.startsWith('/') ? path : `/${path}`
  const segments = normalized.split('/').filter(Boolean)
  const isJoinPath =
    segments.join('/') === 'app/worldcup/groups/join' ||
    segments.join('/') === 'worldcup/groups/join'
  if (!isJoinPath) {
    return normalized
  }
  const rawCode = searchParams.code
  const code =
    typeof rawCode === 'string' ? rawCode : Array.isArray(rawCode) ? rawCode[0] : ''
  const trimmed = String(code ?? '').trim()
  if (!trimmed || !JOIN_CODE_PARAM_VALUE_RE.test(trimmed)) {
    return WC_GROUP_JOIN_APP_PATH
  }
  return `${WC_GROUP_JOIN_APP_PATH}?code=${encodeURIComponent(trimmed.toUpperCase())}`
}

/**
 * Path to use as returnUrl when signing in from the marketing header (stay on site, not /app/).
 */
export function siteMarketingReturnPathFromPathname(pathname: string): string {
  const p = pathname.trim()
  if (p === '/organizations' || p === '/' || p === '/contact' || p === '/packs' || p === '/enterprise')
    return p
  if (p === '') return '/'
  return '/'
}

/**
 * @param raw - returnUrl query value (string or first of array)
 * @returns Safe path for router.replace — /, /organizations, /account-admin, or /app/…
 */
export function getSafePostAuthPath(raw: unknown): string {
  if (raw === undefined || raw === null) {
    return POST_AUTH_DEFAULT_PATH
  }
  const s = typeof raw === 'string' ? raw : Array.isArray(raw) ? raw[0] : ''
  if (!s || typeof s !== 'string') {
    return POST_AUTH_DEFAULT_PATH
  }
  let decoded = s
  try {
    decoded = decodeURIComponent(s)
  } catch {
    return POST_AUTH_DEFAULT_PATH
  }
  const trimmed = decoded.trim()
  if (!trimmed.startsWith('/') || trimmed.includes('://') || trimmed.startsWith('//')) {
    return POST_AUTH_DEFAULT_PATH
  }
  const marketingHomeStripeReturn = getSafeMarketingHomePricingStripeReturnPath(trimmed)
  if (marketingHomeStripeReturn !== null) {
    return marketingHomeStripeReturn
  }
  const marketingHomeSection = getSafeMarketingHomeSectionPath(trimmed)
  if (marketingHomeSection !== null) {
    return marketingHomeSection
  }
  if (MARKETING_RETURN_PATHS.has(trimmed)) {
    return trimmed
  }
  const publicJoinReturn = getSafePublicJoinReturnPath(trimmed)
  if (publicJoinReturn !== null) {
    return publicJoinReturn
  }
  const accountAdminReturn = getSafeAccountAdminReturnPath(trimmed)
  if (accountAdminReturn !== null) {
    return accountAdminReturn
  }
  const wcJoinReturn = getSafeWorldCupGroupJoinReturnPath(trimmed)
  if (wcJoinReturn !== null) {
    return wcJoinReturn
  }
  if (trimmed.startsWith('/app/')) {
    return trimmed
  }
  return POST_AUTH_DEFAULT_PATH
}

/**
 * Append returnUrl query only when it maps to a non-default safe path.
 */
export function appendReturnUrlQuery(path: string, raw: unknown): string {
  const s = typeof raw === 'string' ? raw : Array.isArray(raw) ? raw[0] : ''
  if (!s) {
    return path
  }
  const safe = getSafePostAuthPath(s)
  if (safe === POST_AUTH_DEFAULT_PATH) {
    return path
  }
  const sep = path.includes('?') ? '&' : '?'
  return `${path}${sep}returnUrl=${encodeURIComponent(safe)}`
}
